// Position & Environment Rule Implementation
// Parses environment field for posture cues and creates natural meditation openings

export interface EnvironmentParseResult {
  posture: string | null;
  location: string;
  opening: string;
  environmentalDetails: string[];
}

// Posture keywords to detect in environment field
const POSTURE_KEYWORDS = [
  'sitting', 'sit', 'seated',
  'lying', 'lie', 'laying', 'laid',
  'standing', 'stand',
  'jogging', 'jog', 'running', 'run',
  'lotus', 'lotus pose',
  'kneeling', 'kneel',
  'walking', 'walk'
];

/**
 * Extracts specific environmental details for scattering throughout the session
 * Identifies objects, lighting, clothing, furniture, and descriptive elements
 */
const extractEnvironmentalDetails = (environment: string): string[] => {
  if (!environment?.trim()) return [];

  const text = environment.toLowerCase();
  const details: string[] = [];

  // Common environmental elements to detect
  const patterns = [
    // Lighting
    /\b(dim|bright|soft|warm|cool|natural|fluorescent|overhead|desk)\s+(light|lighting|lamp|bulb)s?\b/g,
    /\b(candle|candlelight|sunlight|moonlight|daylight)s?\b/g,
    
    // Furniture & Objects
    /\b(wooden|metal|glass|leather|fabric)\s+(desk|table|chair|couch|sofa|bed)\b/g,
    /\b(computer|laptop|monitor|keyboard|mouse|phone|notebook|pen|pencil|papers?|documents?)\b/g,
    /\b(window|door|wall|ceiling|floor|carpet|rug)\b/g,
    /\b(plant|flower|tree|picture|photo|artwork|mirror)\b/g,
    
    // Clothing & Personal Items
    /\b(work|business|casual|formal)\s+(tie|shirt|jacket|suit|dress|shoes)\b/g,
    /\b(tie|shirt|jacket|suit|dress|shoes|glasses|watch|jewelry)\b/g,
    
    // Space Characteristics
    /\b(empty|quiet|peaceful|busy|crowded|spacious|small|large|open|closed)\s+(room|office|space|area|hall|conference)\b/g,
    /\b(conference|meeting|break|lunch|rest)\s+(room|area|space)\b/g,
    
    // Descriptive elements
    /\b(comfortable|uncomfortable|soft|hard|warm|cool|cozy|sterile|modern|traditional)\b/g
  ];

  patterns.forEach(pattern => {
    const matches = text.match(pattern);
    if (matches) {
      matches.forEach(match => {
        const cleanMatch = match.trim();
        if (cleanMatch.length > 2 && !details.includes(cleanMatch)) {
          details.push(cleanMatch);
        }
      });
    }
  });

  // Also extract noun phrases that might be environmental details
  const words = text.split(/\s+/);
  for (let i = 0; i < words.length - 1; i++) {
    const phrase = `${words[i]} ${words[i + 1]}`;
    // Skip posture-related phrases
    if (!POSTURE_KEYWORDS.some(keyword => phrase.includes(keyword)) && 
        phrase.length > 4 && 
        !details.includes(phrase) &&
        details.length < 8) { // Limit to prevent overwhelming
      if (/\b(my|the|this|that)\s+\w+/.test(phrase) || 
          /\w+\s+(in|on|at|with|near)\b/.test(phrase)) {
        details.push(phrase);
      }
    }
  }

  return details.slice(0, 6); // Limit to 6 most relevant details
};

/**
 * Parses environment field according to Position & Environment Rule:
 * - Scan only environment field for posture cues
 * - Extract first posture keyword found
 * - Create natural opening that embeds both posture and setting
 * - If no posture found, start with location or neutral breath cue
 */
export const parseEnvironment = (environment: string): EnvironmentParseResult => {
  console.log('🔍 Parsing environment:', environment);
  
  if (!environment || environment.trim().length === 0) {
    console.log('⚠️ Empty environment provided, using defaults');
    return {
      posture: null,
      location: '',
      opening: 'Take a slow breath',
      environmentalDetails: []
    };
  }

  const cleanEnvironment = environment.trim().toLowerCase();
  
  // Extract environmental details first
  const environmentalDetails = extractEnvironmentalDetails(environment);
  console.log('🌟 Environmental details found:', environmentalDetails);
  
  // Find first posture keyword
  let detectedPosture: string | null = null;
  for (const keyword of POSTURE_KEYWORDS) {
    if (cleanEnvironment.includes(keyword)) {
      // Return the base posture (sitting, lying, standing, etc.)
      if (keyword.includes('sit')) detectedPosture = 'sitting';
      else if (keyword.includes('lie') || keyword.includes('lay')) detectedPosture = 'lying';
      else if (keyword.includes('stand')) detectedPosture = 'standing';
      else if (keyword.includes('jog') || keyword.includes('run')) detectedPosture = 'jogging';
      else if (keyword.includes('lotus')) detectedPosture = 'in lotus pose';
      else if (keyword.includes('kneel')) detectedPosture = 'kneeling';
      else if (keyword.includes('walk')) detectedPosture = 'walking';
      else detectedPosture = keyword;
      console.log('✅ Detected posture:', detectedPosture);
      break;
    }
  }

  // Extract location by removing posture references
  let cleanLocation = environment;
  if (detectedPosture) {
    // Remove posture keywords and common connecting words
    const posturePattern = new RegExp(`\\b(${POSTURE_KEYWORDS.join('|')})\\b[\\s-]*`, 'gi');
    cleanLocation = cleanLocation
      .replace(posturePattern, '')
      .replace(/^(in|at|on|by|near)\s+/i, '')
      .replace(/\s+(in|at|on|by|near)\s+/i, ' ')
      .trim();
  }

  console.log('✅ Detected location:', cleanLocation);

  // Create natural opening
  let opening: string;
  if (detectedPosture && cleanLocation) {
    opening = `As you ${detectedPosture === 'in lotus pose' ? 'sit in lotus pose' : detectedPosture} ${cleanLocation}, take a slow breath`;
  } else if (detectedPosture) {
    opening = `As you ${detectedPosture === 'in lotus pose' ? 'sit in lotus pose' : detectedPosture}, take a slow breath`;
  } else if (cleanLocation) {
    opening = `In ${cleanLocation}, take a slow breath`;
  } else {
    opening = 'Take a slow breath';
  }

  const result = {
    posture: detectedPosture,
    location: cleanLocation,
    opening,
    environmentalDetails
  };
  
  console.log('✅ Environment parsing complete:', result);
  return result;
};

/**
 * Creates subtle atmospheric essence from environment description
 * Avoids linear description prompts and direct echoing
 * @param environment - The environment description from the user  
 * @param category - The meditation category (optional, affects integration style)
 */
export const createEnvironmentContext = (environment: string, category?: string): string => {
  console.log('🌙 Creating atmospheric essence for category:', category);
  
  const parsed = parseEnvironment(environment);
  
  if (!environment?.trim()) {
    console.log('⚠️ Empty environment, returning empty context');
    return '';
  }

  // Extract emotional/energetic essence rather than physical description
  const extractAtmosphericEssence = (envText: string, details: string[]): string => {
    const text = envText.toLowerCase();
    let atmosphericHints: string[] = [];
    
    // Detect mood/energy rather than physical space
    if (text.includes('office') || text.includes('work') || text.includes('desk')) {
      atmosphericHints.push('transition energy from productivity mode');
    }
    if (text.includes('bed') || text.includes('bedroom') || text.includes('cozy')) {
      atmosphericHints.push('intimate private sanctuary feeling');
    }
    if (text.includes('quiet') || text.includes('peaceful') || text.includes('calm')) {
      atmosphericHints.push('naturally settled atmosphere');
    }
    if (text.includes('dim') || text.includes('soft light') || text.includes('candle')) {
      atmosphericHints.push('gentle ambient energy');
    }
    if (text.includes('busy') || text.includes('crowded') || text.includes('noisy')) {
      atmosphericHints.push('contrast between external chaos and inner stillness');
    }
    
    return atmosphericHints.join(', ') || 'present moment awareness';
  };

  const atmosphericEssence = extractAtmosphericEssence(environment, parsed.environmentalDetails);
  const isZenAf = category?.toLowerCase().includes('zen') || category?.toLowerCase().includes('af');

  if (isZenAf) {
    return `
ATMOSPHERIC INTEGRATION PERMISSION:
- Feel free to acknowledge their space subtly IF it enhances the therapeutic moment
- You may ignore environment completely if it doesn't serve the session flow
- When used, reference the FEELING of their space rather than describing it
- Atmospheric essence available: ${atmosphericEssence}
- Natural settling posture: ${parsed.posture || 'comfortable position'}

ESSENCE-BASED APPROACH:
- Use environment to create FEELING understood, not HEARING described
- Reference space organically - never linearly or with titles
- Let their atmospheric context enhance raw therapeutic work naturally
- Maximum 1-2 subtle environmental touches if they add therapeutic value

PERMISSION TO IGNORE: Skip environmental references entirely if they don't organically fit the session flow. Therapeutic work takes priority over space acknowledgment.`;
  } else {
    return `
SUBTLE ENVIRONMENTAL PERMISSION:
- Optional gentle acknowledgment of their space if it enhances the session
- Reference the ENERGY/FEELING of their environment, not physical description
- Atmospheric essence: ${atmosphericEssence}  
- Settling foundation: ${parsed.posture || 'comfortable presence'}

ORGANIC INTEGRATION ONLY:
- Use environmental context to deepen presence if it feels natural
- Never force environmental mentions or linear descriptions
- Let space awareness support the meditation naturally or skip entirely
- Focus on therapeutic value over environmental acknowledgment`;
  }
};